/** ArtifactQualitySection — extraction state, confidence, and OCR signals */

import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

interface Props {
  extractionState: string | null
  /** Confidence signal 0..1 or null when not computed */
  confidence?: number | null
  needsOcr?: boolean | null
  isDark: boolean
}

function ConfidencePill({ value, isDark }: { value: number; isDark: boolean }) {
  const pct = Math.round(value * 100)
  const color =
    pct >= 70 ? 'text-green-500' : pct >= 40 ? 'text-yellow-400' : 'text-red-400'
  return (
    <span className={cn('font-mono text-[11px]', color)}>
      {pct}%
    </span>
  )
}

export function ArtifactQualitySection({ extractionState, confidence, needsOcr, isDark }: Props) {
  const { t } = useTranslation('artifact')

  const rowClass = cn('flex items-center gap-2 py-1.5 text-xs')
  const labelClass = cn('w-32 shrink-0 font-medium', isDark ? 'text-neutral-400' : 'text-neutral-500')
  const valueClass = cn('flex-1', isDark ? 'text-neutral-200' : 'text-neutral-800')

  return (
    <section aria-label={t('quality.title')} className="space-y-3">
      <h3 className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
        {t('quality.title')}
      </h3>

      <div className={cn('rounded-lg border p-3 space-y-0.5', isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50')}>
        {/* Extraction state */}
        <div className={rowClass}>
          <span className={labelClass}>{t('quality.extractionState')}</span>
          <span className={cn(valueClass, 'font-mono text-[11px]', stateColor(extractionState, isDark))}>
            {extractionState ?? t('quality.na')}
          </span>
        </div>

        {/* Confidence */}
        <div className={rowClass}>
          <span className={labelClass}>{t('quality.confidence')}</span>
          {confidence != null ? (
            <ConfidencePill value={confidence} isDark={isDark} />
          ) : (
            <span className={cn('text-[11px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>{t('quality.na')}</span>
          )}
        </div>

        {/* Needs OCR */}
        <div className={rowClass}>
          <span className={labelClass}>{t('quality.needsOcr')}</span>
          <span className={cn(valueClass, 'text-[11px]')}>
            {needsOcr == null ? t('quality.na') : needsOcr ? t('quality.yes') : t('quality.no')}
          </span>
        </div>
      </div>
    </section>
  )
}

function stateColor(state: string | null, _isDark: boolean): string {
  if (!state) return 'text-neutral-400'
  if (state === 'ready' || state === 'accepted') return 'text-green-500'
  if (state.startsWith('failed')) return 'text-red-400'
  if (state === 'extracting' || state === 'ocr-retry' || state === 'processing') return 'text-yellow-400'
  return 'text-neutral-400'
}
